import {
  Box,
  Breadcrumbs,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import AmountButton from "../Store/AmountButton";
import useProducts from "../../services/useProducts";
import { Link } from "react-router-dom";
import { IBadge } from "../../types/order";
import { formatProductName } from "../../utils/product";

const API = process.env.REACT_APP_API;

const isNonEmpty = (value?: string | null) =>
  typeof value === "string" && value.trim().length > 0;

const formatPrice = (price: string) => `R$ ${price.replace(".", ",")}`;

const normalizeBadges = (
  badges?: IBadge[] | string[] | string | null,
): IBadge[] => {
  if (!badges) {
    return [];
  }

  if (Array.isArray(badges)) {
    return badges
      .map((badge) => {
        if (typeof badge === "string") {
          const name = badge.trim();
          return name ? { name } : null;
        }

        const name = badge.name?.trim();
        return name ? { ...badge, name } : null;
      })
      .filter((badge): badge is IBadge => Boolean(badge));
  }

  return badges
    .split(",")
    .map((badge) => badge.trim())
    .filter(Boolean)
    .map((name) => ({ name }));
};

const ProductDetails = () => {
  const { productId, slug } = useParams();
  const { data: products, isLoading } = useProducts();

  const offering = useMemo(() => {
    if (!products || !productId) {
      return undefined;
    }

    const parsedId = Number(productId);
    if (Number.isNaN(parsedId)) {
      return undefined;
    }

    return products.find((item) => item.id === parsedId);
  }, [productId, products]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!offering) {
    return (
      <Box sx={{ p: "2rem" }}>
        <Typography variant="h6">Produto não encontrado.</Typography>
      </Box>
    );
  }

  const { product } = offering;
  const productName = formatProductName(product);
  const showDescription = isNonEmpty(product.description);
  const showIngredients = isNonEmpty(product.ingredients);
  const badges = normalizeBadges(product.badges);
  const showBadges = badges.length > 0;

  return (
    <Box sx={{ p: "2rem" }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink component={Link} to={slug ? `/${slug}` : "/"}>
          Voltar para a feira
        </MuiLink>
        <Typography color="text.primary">{productName}</Typography>
      </Breadcrumbs>
      <Grid container spacing={4}>
        <Grid item xs={12} md={5} lg={4}>
          <Card>
            {product.image ? (
              <CardMedia
                image={API + product.image}
                sx={{ height: { xs: "18rem", sm: "22rem", md: "24rem" } }}
                title={`${productName} - ${product.producer.name}`}
              />
            ) : (
              <Box
                sx={{
                  height: { xs: "18rem", sm: "22rem", md: "24rem" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.default",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Sem imagem disponível
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={7} lg={8}>
          <Stack spacing={2}>
            <Typography variant="h4" component="h1">
              {productName}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {product.producer.name}
            </Typography>
            {showBadges && (
              <Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {badges.map((badge, index) => (
                    <Chip
                      key={`${badge.name}-${index}`}
                      label={`${badge.icon ? `${badge.icon} ` : ""}${badge.name}`}
                      sx={{
                        mb: 1,
                        ...(badge.color
                          ? { bgcolor: badge.color, color: "common.white" }
                          : {}),
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            {showDescription && (
              <Box>
                <Typography variant="subtitle1">Descrição</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                  {product.description}
                </Typography>
              </Box>
            )}
            {showIngredients && (
              <Box>
                <Typography variant="subtitle1">Ingredientes</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                  {product.ingredients}
                </Typography>
              </Box>
            )}
            <Divider />
            <Box>
              <Typography variant="h5" sx={{ mb: 1 }}>
                {formatPrice(offering.price)}
              </Typography>
              <AmountButton product={offering} />
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductDetails;
